/**
 * Unified Swap Service
 *
 * Routes swap requests to the appropriate provider based on chains:
 * - Skip Go: EVM, Cosmos, cross-chain
 * - Jupiter: Solana same-chain
 * - STON.fi: TON same-chain
 * - SunSwap: TRON same-chain
 * - BTTC Bridge: TRON <-> ETH/BSC cross-chain
 */

import { selectSwapProvider, getUnsupportedReason, type SwapProvider } from './swapRouter';
import { skipGoApi, getSkipGoDenom, CHAIN_ID_MAP } from './skipGoApi';
import { jupiterApi, getSolanaMint } from './jupiterApi';
import { stonfiApi, getTonTokenAddress } from './stonfiApi';
import { sunswapApi, getTronTokenAddress } from './sunswapApi';
import { bttcBridgeApi, mapChainToBridgeChain } from './bttcBridgeApi';
import { cantonBridgeApi, mapToCantonBridgeChain } from './cantonBridgeApi';
import { canMockHandle, getMockQuote, canMockRoute } from './testnetMockProvider';

export interface SwapQuoteRequest {
  fromSymbol: string;
  fromChain: string;
  toSymbol: string;
  toChain: string;
  amount: string;          // Human readable amount
  fromDecimals: number;
  toDecimals: number;
  fromContractAddress?: string;
  toContractAddress?: string;
  slippagePercent?: number;
}

export interface SwapQuote {
  provider: SwapProvider;
  fromSymbol: string;
  fromChain: string;
  toSymbol: string;
  toChain: string;
  amountIn: string;        // Human readable
  amountOut: string;       // Human readable
  amountInRaw: string;     // Smallest units
  amountOutRaw: string;    // Smallest units
  priceImpact?: string;
  estimatedFees?: string;
  estimatedTime?: number;  // Seconds
  route?: unknown;         // Provider-specific route data
}

export interface SwapExecuteRequest {
  quote: SwapQuote;
  userAddresses: Record<string, string>; // Chain -> address mapping
  slippagePercent?: number;
}

export interface SwapExecuteResult {
  provider: SwapProvider;
  transactions: Array<{
    chainType: 'evm' | 'solana' | 'ton' | 'tron' | 'cosmos';
    chainId: string;
    transaction: unknown;
  }>;
}

/**
 * Convert human readable amount to smallest units
 */
function toSmallestUnit(amount: string, decimals: number): string {
  const num = parseFloat(amount);
  if (isNaN(num)) return '0';
  return Math.floor(num * Math.pow(10, decimals)).toString();
}

/**
 * Convert smallest units to human readable
 */
function fromSmallestUnit(amount: string, decimals: number): string {
  const num = parseFloat(amount);
  if (isNaN(num)) return '0';
  return (num / Math.pow(10, decimals)).toFixed(6);
}

class SwapService {
  /**
   * Get a swap quote from the appropriate provider
   */
  async getQuote(request: SwapQuoteRequest): Promise<SwapQuote> {
    // Testnet mock: intercept before real provider dispatch
    if (canMockHandle(request)) {
      return getMockQuote(request);
    }

    const provider = selectSwapProvider(request.fromChain, request.toChain);

    if (provider === 'unsupported') {
      throw new Error(getUnsupportedReason(request.fromChain, request.toChain));
    }

    const amountInRaw = toSmallestUnit(request.amount, request.fromDecimals);
    const slippage = request.slippagePercent || 0.5;

    switch (provider) {
      case 'skipgo':
        return this.getSkipGoQuote(request, amountInRaw, slippage);
      case 'jupiter':
        return this.getJupiterQuote(request, amountInRaw, slippage);
      case 'stonfi':
        return this.getStonfiQuote(request, amountInRaw, slippage);
      case 'sunswap':
        return this.getSunswapQuote(request, amountInRaw, slippage);
      case 'bttc':
        return this.getBTTCBridgeQuote(request, amountInRaw);
      case 'canton':
        return this.getCantonBridgeQuote(request, amountInRaw);
      default:
        throw new Error(`Unknown provider: ${provider}`);
    }
  }

  /**
   * Get quote from Skip Go
   */
  private async getSkipGoQuote(
    request: SwapQuoteRequest,
    amountInRaw: string,
    _slippage: number
  ): Promise<SwapQuote> {
    const sourceChainId = CHAIN_ID_MAP[request.fromChain];
    const destChainId = CHAIN_ID_MAP[request.toChain];

    if (!sourceChainId || !destChainId) {
      throw new Error('Chain not supported by Skip Go');
    }

    const route = await skipGoApi.getRoute({
      sourceAssetDenom: getSkipGoDenom(request.fromSymbol, sourceChainId, request.fromContractAddress),
      sourceAssetChainId: sourceChainId,
      destAssetDenom: getSkipGoDenom(request.toSymbol, destChainId, request.toContractAddress),
      destAssetChainId: destChainId,
      amountIn: amountInRaw,
    });

    return {
      provider: 'skipgo',
      fromSymbol: request.fromSymbol,
      fromChain: request.fromChain,
      toSymbol: request.toSymbol,
      toChain: request.toChain,
      amountIn: request.amount,
      amountOut: fromSmallestUnit(route.estimatedAmountOut || route.amountOut, request.toDecimals),
      amountInRaw,
      amountOutRaw: route.estimatedAmountOut || route.amountOut,
      priceImpact: route.swapPriceImpactPercent,
      estimatedFees: route.estimatedFees?.reduce((sum, f) => sum + parseFloat(f.usdAmount || '0'), 0).toFixed(2),
      estimatedTime: route.estimatedRouteDurationSeconds,
      route,
    };
  }

  /**
   * Get quote from Jupiter (Solana)
   */
  private async getJupiterQuote(
    request: SwapQuoteRequest,
    amountInRaw: string,
    slippage: number
  ): Promise<SwapQuote> {
    const inputMint = getSolanaMint(request.fromSymbol, request.fromContractAddress);
    const outputMint = getSolanaMint(request.toSymbol, request.toContractAddress);

    if (!inputMint || !outputMint) {
      throw new Error(`Unknown Solana token: ${!inputMint ? request.fromSymbol : request.toSymbol}`);
    }

    const quote = await jupiterApi.getQuote({
      inputMint,
      outputMint,
      amount: amountInRaw,
      slippageBps: Math.floor(slippage * 100),
    });

    return {
      provider: 'jupiter',
      fromSymbol: request.fromSymbol,
      fromChain: request.fromChain,
      toSymbol: request.toSymbol,
      toChain: request.toChain,
      amountIn: request.amount,
      amountOut: fromSmallestUnit(quote.outAmount, request.toDecimals),
      amountInRaw,
      amountOutRaw: quote.outAmount,
      priceImpact: quote.priceImpactPct,
      route: quote,
    };
  }

  /**
   * Get quote from STON.fi (TON)
   */
  private async getStonfiQuote(
    request: SwapQuoteRequest,
    amountInRaw: string,
    slippage: number
  ): Promise<SwapQuote> {
    const offerAddress = getTonTokenAddress(request.fromSymbol, request.fromContractAddress);
    const askAddress = getTonTokenAddress(request.toSymbol, request.toContractAddress);

    if (!offerAddress || !askAddress) {
      throw new Error(`Unknown TON token: ${!offerAddress ? request.fromSymbol : request.toSymbol}`);
    }

    const simulation = await stonfiApi.getSwapSimulation({
      offerAddress,
      askAddress,
      offerUnits: amountInRaw,
      slippageTolerance: (slippage / 100).toString(),
    });

    return {
      provider: 'stonfi',
      fromSymbol: request.fromSymbol,
      fromChain: request.fromChain,
      toSymbol: request.toSymbol,
      toChain: request.toChain,
      amountIn: request.amount,
      amountOut: fromSmallestUnit(simulation.ask_units, request.toDecimals),
      amountInRaw,
      amountOutRaw: simulation.ask_units,
      priceImpact: simulation.price_impact,
      estimatedFees: simulation.fee_units,
      route: simulation,
    };
  }

  /**
   * Get quote from SunSwap (TRON)
   */
  private async getSunswapQuote(
    request: SwapQuoteRequest,
    amountInRaw: string,
    slippage: number
  ): Promise<SwapQuote> {
    const tokenIn = getTronTokenAddress(request.fromSymbol, request.fromContractAddress);
    const tokenOut = getTronTokenAddress(request.toSymbol, request.toContractAddress);

    if (!tokenIn || !tokenOut) {
      throw new Error(`Unknown TRON token: ${!tokenIn ? request.fromSymbol : request.toSymbol}`);
    }

    const route = await sunswapApi.getQuote({
      tokenIn,
      tokenOut,
      amountIn: amountInRaw,
      slippage,
    });

    return {
      provider: 'sunswap',
      fromSymbol: request.fromSymbol,
      fromChain: request.fromChain,
      toSymbol: request.toSymbol,
      toChain: request.toChain,
      amountIn: request.amount,
      amountOut: fromSmallestUnit(route.amountOut, request.toDecimals),
      amountInRaw,
      amountOutRaw: route.amountOut,
      priceImpact: route.priceImpact,
      estimatedFees: route.fee,
      route,
    };
  }

  /**
   * Get quote from BTTC Bridge (TRON <-> ETH/BSC)
   */
  private async getBTTCBridgeQuote(
    request: SwapQuoteRequest,
    amountInRaw: string
  ): Promise<SwapQuote> {
    // Map chain names to BTTC chain types
    const fromBridgeChain = mapChainToBridgeChain(request.fromChain);
    const toBridgeChain = mapChainToBridgeChain(request.toChain);

    if (!fromBridgeChain || !toBridgeChain) {
      throw new Error(`BTTC bridge doesn't support ${request.fromChain} or ${request.toChain}`);
    }

    // BTTC bridge only works for same-symbol tokens (e.g., USDT -> USDT)
    if (request.fromSymbol !== request.toSymbol) {
      throw new Error(`BTTC bridge only supports same-token transfers (${request.fromSymbol} -> ${request.toSymbol})`);
    }

    const bridgeQuote = await bttcBridgeApi.getQuote({
      fromChain: fromBridgeChain,
      toChain: toBridgeChain,
      tokenSymbol: request.fromSymbol,
      amount: request.amount,
    });

    if (!bridgeQuote.isSupported) {
      throw new Error(bridgeQuote.error || 'Bridge route not available');
    }

    return {
      provider: 'bttc',
      fromSymbol: request.fromSymbol,
      fromChain: request.fromChain,
      toSymbol: request.toSymbol,
      toChain: request.toChain,
      amountIn: request.amount,
      amountOut: bridgeQuote.amountOut,
      amountInRaw,
      amountOutRaw: amountInRaw, // 1:1 bridge
      priceImpact: '0',
      estimatedFees: bridgeQuote.estimatedFee,
      estimatedTime: bridgeQuote.estimatedTime,
      route: bridgeQuote,
    };
  }

  /**
   * Get quote from Canton Bridge (Canton CC <-> Base USDC)
   */
  private async getCantonBridgeQuote(
    request: SwapQuoteRequest,
    amountInRaw: string
  ): Promise<SwapQuote> {
    // Map chain names to Canton bridge chain types
    const fromBridgeChain = mapToCantonBridgeChain(request.fromChain);
    const toBridgeChain = mapToCantonBridgeChain(request.toChain);

    if (!fromBridgeChain || !toBridgeChain) {
      throw new Error(`Canton bridge doesn't support ${request.fromChain} or ${request.toChain}`);
    }

    // Canton bridge only works for CC <-> USDC (case-insensitive)
    const fromUpper = request.fromSymbol.toUpperCase();
    const toUpper = request.toSymbol.toUpperCase();
    const validPair = (fromUpper === 'CC' && toUpper === 'USDC') ||
                      (fromUpper === 'USDC' && toUpper === 'CC');
    if (!validPair) {
      throw new Error(`Canton bridge only supports CC <-> USDC swaps (got ${request.fromSymbol} -> ${request.toSymbol})`);
    }

    const bridgeQuote = await cantonBridgeApi.getQuote({
      fromChain: fromBridgeChain,
      toChain: toBridgeChain,
      fromSymbol: request.fromSymbol,
      toSymbol: request.toSymbol,
      amount: request.amount,
    });

    if (!bridgeQuote.isSupported) {
      throw new Error(bridgeQuote.error || 'Canton bridge route not available');
    }

    // Convert amountOut to raw units
    const amountOutRaw = toSmallestUnit(bridgeQuote.amountOut, request.toDecimals);

    return {
      provider: 'canton',
      fromSymbol: request.fromSymbol,
      fromChain: request.fromChain,
      toSymbol: request.toSymbol,
      toChain: request.toChain,
      amountIn: request.amount,
      amountOut: bridgeQuote.amountOut,
      amountInRaw,
      amountOutRaw,
      priceImpact: '0', // Bridge has no price impact
      estimatedFees: bridgeQuote.estimatedFee,
      estimatedTime: bridgeQuote.estimatedTime,
      route: bridgeQuote,
    };
  }

  /**
   * Get the provider that will be used for a swap
   */
  getProvider(fromChain: string, toChain: string): SwapProvider {
    return selectSwapProvider(fromChain, toChain);
  }

  /**
   * Check if a route is supported
   */
  isSupported(fromChain: string, toChain: string): boolean {
    if (canMockRoute(fromChain, toChain)) return true;
    return selectSwapProvider(fromChain, toChain) !== 'unsupported';
  }

  /**
   * Get reason why a route is not supported
   */
  getUnsupportedReason(fromChain: string, toChain: string): string {
    if (canMockRoute(fromChain, toChain)) return '';
    return getUnsupportedReason(fromChain, toChain);
  }
}

export const swapService = new SwapService();
export { SwapService };
